import { transformUrl, useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization.tsx";
import { useUser } from "@/client/backend/user.tsx";
import {
  fetchBuildLogContent,
  fetchBuildLogMetadata,
  useBazelBuildLogs,
  useBuildLogs,
  type BazelBuildLog,
  type BazelInvocationMeta,
  type BuildLogLine,
  type InstanceBuildLog,
} from "@/client/director/buildLogs.tsx";
import { findOwningOrganization, orgRequestOptions } from "@/client/director/org.ts";
import { BuildCacheStatus } from "@/components/BuildCacheStatus";
import { DeviceInstallRelay } from "@/components/device-install/DeviceInstallRelay";
import { AppShell, PageHeader } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { XtermLogPanel, joinChunks } from "@/components/XtermLogPanel.tsx";
import { cacheOperationLabel } from "@/lib/build-cache";
import { cacheLabel, cacheStats, barToneClass, isTerminalStatus, runnerTone, type BarTone } from "@/lib/bazel";
import { formatDurationMs, formatRelativeTime, formatSize } from "@/lib/format";
import { platformFromInstanceId } from "@/lib/instance-platform.ts";
import { cn } from "@/lib/utils";
import type { Limrun } from "@limrun/api";
import { APIError } from "@limrun/api/core/error";
import { Check, ChevronRight, Copy, Database, Download } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSWR from "swr";

// Status values limbuild reports in the meta line, plus QUEUED/RUNNING which
// only ever appear on live SSE events (they're never persisted to R2).
type ExecStatus =
	| "QUEUED"
	| "RUNNING"
	| "SUCCEEDED"
	| "FAILED"
	| "CANCELLED"
	| string;

// liveExec represents the currently-active build streamed via SSE. It has no
// downloadUrl (historical entries in R2 are persisted only after the build
// finishes and runs its deferred upload).
type LiveExec = {
	id: string;
	status: ExecStatus;
	startedAt: string;
	lines: BuildLogLine[];
};

type LiveExecMeta = {
	id?: string;
	status?: ExecStatus;
	startedAt?: string;
};

function formatDuration(
	startedAt: string | undefined | null,
	finishedAt: string | undefined | null,
): string {
	if (!startedAt) return "";
	const start = new Date(startedAt).getTime();
	const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
	const secs = Math.round((end - start) / 1000);
	return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}


// HistEntry is a finished build in the merged list: an xcode or gradle build,
// or a Bazel RBE invocation. They share id/status/startedAt/downloadUrl shapes.
type HistEntry =
	| { kind: "historical"; log: InstanceBuildLog }
	| { kind: "bazel"; log: BazelBuildLog };

// histStartMs is the chronological sort key (ms since epoch). For xcode builds
// it derives from the id ("build-{unixNano}"), which is the director's canonical
// key order, so the xcode-only list is byte-for-byte unchanged. Bazel invocation
// ids are UUIDs, so they sort by startedAt.
function histStartMs(entry: HistEntry): number {
	if (entry.kind === "historical") {
		const m = /build-(\d+)/.exec(entry.log.id);
		if (m) return Number(m[1]) / 1e6;
	}
	const startedAt = entry.log.startedAt;
	if (startedAt) {
		const t = new Date(startedAt).getTime();
		if (Number.isFinite(t)) return t;
	}
	return 0;
}


export function ExecStatusBadge({ status }: { status: ExecStatus }) {
	const normalized = status.toUpperCase();
	return (
		<Badge
			variant="outline"
			className={cn("border px-2 py-0.5 text-xs font-medium", {
				"border-yellow-200 bg-yellow-50 text-yellow-700":
					normalized === "QUEUED",
				"border-blue-200 bg-blue-50 text-blue-700":
					normalized === "RUNNING",
				"border-green-200 bg-green-50 text-green-700":
					["SUCCEEDED", "RESTORED", "PUBLISHED"].includes(normalized),
				"border-red-200 bg-red-50 text-red-700": normalized === "FAILED",
				"border-gray-200 bg-gray-50 text-gray-700":
					["CANCELLED", "INCOMPLETE", "SKIPPED", "DISABLED", "UNSUPPORTED"].includes(normalized),
			})}
		>
			{status.toLowerCase()}
		</Badge>
	);
}

// decodeSSEData reverses the daemon's JSON-encoded SSE payload so control bytes
// (Bazel's curses \r and ANSI) arrive intact. Tolerant of an older daemon that
// streamed raw text: a non-JSON or non-string payload falls through unchanged.
export function decodeSSEData(raw: string): string {
	try {
		const parsed = JSON.parse(raw);
		if (typeof parsed === "string") return parsed;
	} catch {
		// Older daemon: raw text payload (a lone \r was already lost on the wire).
	}
	return raw;
}

export function LiveTimer({ startedAt }: { startedAt: string }) {
	const [, setTick] = useState(0);
	useEffect(() => {
		const id = setInterval(() => setTick((t) => t + 1), 1000);
		return () => clearInterval(id);
	}, []);
	const startedAtMs = new Date(startedAt).getTime();
	const secs = Number.isFinite(startedAtMs)
		? Math.max(0, Math.round((Date.now() - startedAtMs) / 1000))
		: 0;
	const display = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
	return <span role="timer" className="shrink-0 text-xs text-muted-foreground">{display}</span>;
}


// InstanceView is the slice of an xcode or gradle instance this page reads;
// both platforms' status carries the same fields.
type InstanceView = {
	status: { state: string; apiUrl?: string; token?: string };
};

// Poll while the instance is still coming up so the page advances to ready
// without a manual refresh. Positive list: "unknown" (xcode's public mapping
// for skipped instances) would otherwise poll forever. Module scope because
// SWR re-arms the poll timer whenever this function's identity changes.
const instanceRefreshInterval = (latest: InstanceView | undefined) =>
	latest && (latest.status.state === "creating" || latest.status.state === "assigned")
		? 3_000
		: 0;

// fetchInstance dispatches the org-scoped instance GET on the instance's
// platform, shared by useInstance and the wrong-org probe on this page (the
// history hook in buildLogs.tsx carries its own endpoint dispatch).
function fetchInstance(
	client: Limrun,
	id: string,
	options?: ReturnType<typeof orgRequestOptions>,
): Promise<InstanceView> {
	return platformFromInstanceId(id) === "gradle"
		? client.gradleInstances.get(id, options)
		: client.xcodeInstances.get(id, options);
}

// useInstance fetches the instance details via the director SDK, dispatching
// on the instance's platform (gradle vs xcode). A 404 (read-after-create
// lag, or a purged instance) surfaces as an error while the page keeps
// showing "Loading..."; SWR's error retry and focus revalidation recover.
function useInstance(
	instanceId: string | undefined,
	organizationTid: string | undefined,
	poll = false,
): {
	instance: InstanceView | "loading" | undefined;
	error?: Error;
} {
	const auth = useAuth();
	const { data, error } = useSWR(
		instanceId ? ["buildsInstance", instanceId, organizationTid] : null,
		async (): Promise<InstanceView> =>
			fetchInstance(auth.regionClient, instanceId as string, orgRequestOptions(organizationTid)),
		{
			refreshInterval: (latest) =>
				poll && latest?.status.state !== "terminated" ? 5_000 : instanceRefreshInterval(latest),
		},
	);
	return {
		instance: instanceId ? (data ?? "loading") : undefined,
		error: error instanceof Error ? error : undefined,
	};
}

// useLiveExec opens an SSE connection to {apiUrl}/exec/active/events and
// collects stdout/stderr/command events. Retry idle or interrupted streams
// and retain completed output until history arrives or another build starts.
// A build already running when the page opens is fully covered: the daemon
// replays the stream from its first frame before tailing live.
function useLiveExec(
	apiUrl: string | undefined,
	token: string | undefined,
): { liveExec: LiveExec | undefined; clearLiveExec: () => void; reconnecting: boolean } {
	const [liveExec, setLiveExec] = useState<LiveExec>();
	const [reconnecting, setReconnecting] = useState(false);

	useEffect(() => {
		setReconnecting(false);
		if (!apiUrl) {
			setLiveExec((current) => current && !isTerminalStatus(current.status)
				? { ...current, status: "INCOMPLETE" } : current);
			return;
		}
		setLiveExec(undefined);
		let disposed = false;
		let source: EventSource | undefined;
		let retry: ReturnType<typeof setTimeout> | undefined;
		const connect = () => {
			if (disposed) return;
			const url = new URL(`${apiUrl}/exec/active/events`);
			if (token) url.searchParams.set("token", token);
			url.searchParams.set("encoding", "json");
			const es = new EventSource(url.toString());
			source = es;
			// Every connection replays from the start. Keep the previous output
			// visible until a new stream arrives, but never append a replay to it.
			let current: LiveExec | undefined;
			let closed = false;
			const reconnect = () => {
				if (closed) return;
				closed = true;
				es.close();
				if (!disposed) retry = setTimeout(connect, 3_000);
			};
			const publish = () => {
				if (disposed || closed) return;
				setReconnecting(false);
				setLiveExec(current);
			};
			const append = (type: "command" | "stdout" | "stderr", data: string) => {
				current = {
					...(current ?? { id: "active", status: "RUNNING", startedAt: new Date().toISOString() }),
					lines: [...(current?.lines ?? []), { type, data }],
				};
				publish();
			};
			es.addEventListener("meta", (e) => {
				try {
					const meta = JSON.parse(decodeSSEData(e.data)) as LiveExecMeta;
					current = {
						id: meta.id ?? current?.id ?? "active",
						status: meta.status ?? current?.status ?? "RUNNING",
						startedAt: meta.startedAt ?? current?.startedAt ?? new Date().toISOString(),
						lines: current?.lines ?? [],
					};
					publish();
				} catch {
					/* Output remains readable when metadata is malformed. */
				}
			});
			es.addEventListener("command", (e) => append("command", decodeSSEData(e.data)));
			es.addEventListener("stdout", (e) => append("stdout", decodeSSEData(e.data)));
			es.addEventListener("stderr", (e) => append("stderr", decodeSSEData(e.data)));
			es.addEventListener("exitCode", (e) => {
				if (current) {
					const code = Number.parseInt(decodeSSEData(e.data), 10);
					current = {
						...current,
						status: code === 0 ? "SUCCEEDED" : code < 0 ? "CANCELLED" : "FAILED",
					};
					publish();
				}
				reconnect();
			});
			es.onerror = () => {
				if (!disposed) setReconnecting(true);
				reconnect();
			};
		};
		connect();
		return () => {
			disposed = true;
			clearTimeout(retry);
			source?.close();
		};
	}, [apiUrl, token]);

	const clearLiveExec = useCallback(() => setLiveExec(undefined), []);
	return { liveExec, clearLiveExec, reconnecting };
}

// ActiveBazelInvocation is an in-flight RBE invocation reported by the instance's
// /rbe/builds/active endpoint (a BazelInvocationSummary, status RUNNING).
type ActiveBazelInvocation = {
	id: string;
	status: string;
	startedAt?: string;
};

// useActiveBazelInvocations polls the instance for in-flight Bazel invocations so
// a running build appears in the list before it finishes. Errors resolve to an
// empty list (an instance without the endpoint just shows no live invocations).
function useActiveBazelInvocations(
	apiUrl: string | undefined,
	token: string | undefined,
	enabled: boolean,
): ActiveBazelInvocation[] {
	const { data } = useSWR<ActiveBazelInvocation[]>(
		enabled && apiUrl ? ["activeBazel", apiUrl] : null,
		async () => {
			try {
				const url = new URL(`${apiUrl}/rbe/builds/active`);
				if (token) url.searchParams.set("token", token);
				const res = await fetch(url.toString());
				if (!res.ok) return [];
				const raw = (await res.json()) as Array<{
					invocationId: string;
					status: string;
					startedAt?: string;
				}>;
				return raw.map((s) => ({
					id: s.invocationId,
					status: s.status,
					startedAt: s.startedAt,
				}));
			} catch {
				return [];
			}
		},
		{ refreshInterval: 2_000 },
	);
	// Stable empty reference so consumers depending on this in effect deps do not
	// re-run every render before the first fetch resolves.
	return data ?? NO_ACTIVE_BAZEL;
}

const NO_ACTIVE_BAZEL: ActiveBazelInvocation[] = [];

// useLiveBazelExec streams a specific running invocation's console output by
// subscribing to its instance SSE (the recorder publishes stdout/stderr keyed by
// invocation id, terminated by an "end" frame). Returns the accumulated lines.
function useLiveBazelExec(
	apiUrl: string | undefined,
	token: string | undefined,
	invocationId: string | undefined,
	onTerminal?: (invocationId: string, status: string) => void,
): BuildLogLine[] {
	const [lines, setLines] = useState<BuildLogLine[]>([]);
	const linesRef = useRef<BuildLogLine[]>([]);
	const frameRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		linesRef.current = [];
		setLines([]);
		if (!apiUrl || !invocationId) return;
		const url = new URL(`${apiUrl}/exec/${invocationId}/events`);
		if (token) url.searchParams.set("token", token);
		url.searchParams.set("encoding", "json"); // lossless framing for Bazel's curses output
		const es = new EventSource(url.toString());
		// Accumulate in place and flush at most once per animation frame, so a
		// fast-streaming build neither copies the whole array per frame (O(n^2))
		// nor re-renders the pane once per BES event.
		const flush = () => {
			frameRef.current = undefined;
			setLines(linesRef.current.slice());
		};
		const append = (type: "stdout" | "stderr", data: string) => {
			linesRef.current.push({ type, data });
			if (frameRef.current === undefined) {
				frameRef.current = requestAnimationFrame(flush);
			}
		};
		es.addEventListener("stdout", (e) => append("stdout", decodeSSEData(e.data)));
		es.addEventListener("stderr", (e) => append("stderr", decodeSSEData(e.data)));
		// Close only on the terminal "end" frame. A transient drop is left to
		// EventSource's built-in auto-reconnect so a blip over a long build does
		// not permanently freeze the live pane; teardown closes on unmount. The end
		// frame carries the terminal summary, so settle the bridged entry's status
		// immediately instead of waiting for the historical record to land.
		es.addEventListener("end", (e) => {
			try {
				const summary = JSON.parse(decodeSSEData(e.data)) as { status?: string };
				if (invocationId && summary.status) onTerminal?.(invocationId, summary.status);
			} catch {
				// Ignore malformed end metadata; closing still hands off to history.
			}
			es.close();
		});
		return () => {
			es.close();
			if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
		};
	}, [apiUrl, token, invocationId, onTerminal]);

	return lines;
}

// useHistoricalLogContent fetches the raw output for a persisted build and,
// for Bazel records, its separate rich metadata sidecar. Keyed by id because
// presigned URLs rotate while persisted content is immutable.
function useHistoricalLogContent(
	log: { id: string; downloadUrl: string; metadataDownloadUrl?: string } | undefined,
): {
	lines: BuildLogLine[] | "loading";
	meta?: BazelInvocationMeta;
	error?: Error;
} {
	const { data, error } = useSWR(
		log ? ["buildLogContent", log.id] : null,
		async () => {
			const current = log!;
			const lines = await fetchBuildLogContent(current.downloadUrl);
			const meta = current.metadataDownloadUrl
				? await fetchBuildLogMetadata<BazelInvocationMeta>(current.metadataDownloadUrl)
				: undefined;
			return { lines, meta };
		},
	);
	return {
		lines: data?.lines ?? "loading",
		meta: data?.meta,
		error: error instanceof Error ? error : undefined,
	};
}

// formatBuildLabel numbers builds independently of cache operations.
function formatBuildLabel(index: number, total: number): string {
	return `#${total - index}`;
}

export function BuildLogs({ instanceId, compact = false }: { instanceId: string; compact?: boolean }) {
	// Gradle gets persisted history like xcode; only the Bazel/RBE surface and
	// the real-device install relay stay xcode-only (gated on !isGradle below).
	const isGradle = !!instanceId && platformFromInstanceId(instanceId) === "gradle";
	const [searchParams] = useSearchParams();
	const { user } = useUser();
	const [organization, setOrganization] = useOrganization();
	const organizationTid = organization !== "loading" ? organization.tid : undefined;
	const auth = useAuth();
	const canInstallToRealDevice =
		user !== "loading" && user.emailAddress.endsWith("@limrun.com");

	const devLimbuildApiUrl =
		!compact && import.meta.env.DEV ? searchParams.get("limbuildApiUrl") || undefined : undefined;
	const devLimbuildToken =
		!compact && import.meta.env.DEV ? searchParams.get("limbuildToken") || undefined : undefined;
	const devRegistryApiUrl =
		!compact && import.meta.env.DEV ? searchParams.get("registryApiUrl") || undefined : undefined;
	const configuredRegistryApiUrl = import.meta.env.VITE_REGISTRY_API_ENDPOINT as string | undefined;
	const { instance, error: instanceError } = useInstance(
		devLimbuildApiUrl ? undefined : instanceId,
		organizationTid,
		compact,
	);

	// Wrong-org recovery, mirroring the preview page: an instance link is often
	// opened under a different organization than the one that owns it, and the
	// org-scoped GET then 404s forever. Probe the user's other organizations
	// and switch to the owner when one answers. The probe arms only after the
	// 404 has persisted for a moment, because the same 404 fires on
	// read-after-create lag, where an immediate probe (and an immediate
	// not-found verdict) would be premature.
	const instance404 = instanceError instanceof APIError && instanceError.status === 404;
	const [probeArmedAt, setProbeArmedAt] = useState<number | undefined>(undefined);
	useEffect(() => {
		if (!instance404) {
			setProbeArmedAt(undefined);
			return;
		}
		const timer = setTimeout(() => setProbeArmedAt(Date.now()), 2_500);
		return () => clearTimeout(timer);
	}, [instance404]);
	const probeCandidates = useMemo(
		() =>
			user === "loading" || organization === "loading"
				? undefined
				: user.organizations.filter((o) => o.tid !== organization.tid),
		[user, organization],
	);
	// SWR keyed on (instance, selected org): navigating away or switching org
	// discards the verdict with the key (no stale not-found, and a probe that
	// resolves after unmount cannot flip the org). Resolves null when no org
	// owns it; for a user with no other orgs the single run settles that null
	// verdict directly, which is the proper not-found state for a purged or
	// mistyped link. Re-probing while the 404 persists covers a probe that
	// raced the director's read-after-create lag, so it only matters when
	// there are candidates and stops once the arming window has comfortably
	// outlived that lag; past it, the null verdict is final and only the
	// instance GET's own retries keep running (a late-appearing instance
	// still replaces the soft not-found message when its GET succeeds).
	const { data: owningOrganization } = useSWR(
		!compact && probeArmedAt && instance404 && organization !== "loading" && probeCandidates
			? ["instanceOwnerProbe", instanceId, organization.tid]
			: null,
		async () => {
			const owner = await findOwningOrganization(probeCandidates ?? [], async (candidate) => {
				await fetchInstance(auth.regionClient, instanceId as string, orgRequestOptions(candidate.tid));
				return true;
			});
			return owner ?? null;
		},
		{
			refreshInterval: (latest) =>
				latest === null &&
				probeCandidates?.length &&
				probeArmedAt &&
				Date.now() - probeArmedAt < 30_000
					? 10_000
					: 0,
		},
	);
	useEffect(() => {
		if (owningOrganization) setOrganization(owningOrganization);
	}, [owningOrganization, setOrganization]);
	const instanceNotFound = owningOrganization === null;
	const instanceReady =
		!!devLimbuildApiUrl ||
		(instance !== "loading" && instance?.status.state === "ready");
	const instanceStarting =
		instance !== "loading" &&
		(instance?.status.state === "creating" ||
			instance?.status.state === "assigned");
	const instanceTerminated =
		instance !== "loading" && instance?.status.state === "terminated";

	// Live build via SSE (only when instance is ready).
	const apiUrl =
		devLimbuildApiUrl ??
		(instance !== "loading" && instance?.status.apiUrl
			? instance.status.apiUrl
			: undefined);
	const instanceToken =
		devLimbuildToken ??
		(instance !== "loading" && instance?.status.token
			? instance.status.token
			: undefined);
	const registryApiUrl = devRegistryApiUrl ?? transformUrl(configuredRegistryApiUrl);
	const { liveExec, clearLiveExec, reconnecting } = useLiveExec(
		instanceReady ? apiUrl : undefined,
		instanceReady ? instanceToken : undefined,
	);

	const liveExecFinished = liveExec !== undefined && isTerminalStatus(liveExec.status);

	// Persisted history is platform-wide (xcode and gradle); the Bazel/RBE
	// surface stays xcode-only, so every Bazel data source carries its own
	// !isGradle term rather than riding historyEnabled.
	const historyEnabled = !devLimbuildApiUrl && !!instanceId && !!organizationTid;
	const bazelEnabled = !isGradle && instanceReady;

	// In-flight Bazel invocations polled from the instance, so a running build
	// appears live before it lands in the historical list.
	const activeBazel = useActiveBazelInvocations(
		bazelEnabled ? apiUrl : undefined,
		bazelEnabled ? instanceToken : undefined,
		bazelEnabled,
	);
	// bridgedBazel retains a finished invocation until its historical record lands;
	// declared here so the refresh below stays fast during that handoff window.
	const [bridgedBazel, setBridgedBazel] = useState<
		Array<ActiveBazelInvocation & { droppedAt?: number }>
	>([]);
	// Poll history fast while a Bazel build is in flight OR a bridged invocation is
	// still awaiting its historical record, so the live->historical handoff is quick.
	const bazelHistRefreshMs =
		liveExecFinished || activeBazel.length > 0 || bridgedBazel.length > 0
			? 2_000
			: instanceReady
				? 10_000
				: undefined;

	// Historical builds from the director. Polled while the instance is live
	// so recently-finished builds appear without a manual refresh. Poll faster
	// when a live build just finished so the transition is seamless.
	const { data: historical, error: historyError } = useBuildLogs({
		instanceId,
		organizationTid,
		enabled: historyEnabled,
		refreshIntervalMs: liveExecFinished ? 2_000 : 5_000,
	});
	// Bazel RBE invocations from the director, gated by design: failures resolve
	// to an empty list (see useBazelBuildLogs) so the page is unchanged when the
	// backend lacks the endpoint or has no records. Unlike history this stays
	// xcode-only: gradle instances never query the Bazel endpoint.
	const { data: bazel } = useBazelBuildLogs({
		instanceId,
		organizationTid,
		enabled: historyEnabled && !isGradle,
		refreshIntervalMs: bazelHistRefreshMs,
	});
	// An errored history fetch stops counting as loading: the page then renders
	// with an empty history section instead of hanging the whole view (live SSE
	// included) on "Loading..." while SWR retries toward recovery.
	const historicalLoading = historyEnabled && historical === "loading" && !historyError;

	// Keep the finished live log until its own record lands, even when cache logs arrive first.
	useEffect(() => {
		if (historical !== "loading" && liveExecFinished && historical.some((record) => record.id === liveExec?.id)) {
			clearLiveExec();
		}
	}, [liveExecFinished, historical, liveExec?.id, clearLiveExec]);

	const historicalList = historical === "loading" ? [] : historical;
	const bazelList = bazel === "loading" ? [] : bazel;

	// Bridge: a finished invocation drops from activeBazel (2s poll) before its
	// historical record lands (director upload + ranged GET), which would blank
	// the selection and live log mid-handoff. Retain recently-dropped invocations
	// until their historical twin appears (or a grace window passes), mirroring the
	// xcode clearLiveExec bridge.
	// Keyed on the stable SWR `bazel` reference (not the per-render bazelList) so
	// this Set is stable between renders and does not retrigger the bridge effect.
	const bazelHistIds = useMemo(
		() => new Set(bazel === "loading" ? [] : bazel.map((l) => l.id)),
		[bazel],
	);
	useEffect(() => {
		const now = Date.now();
		setBridgedBazel((prev) => {
			const activeIds = new Set(activeBazel.map((i) => i.id));
			const out = new Map<string, ActiveBazelInvocation & { droppedAt?: number }>();
			// retain previously-seen invocations that dropped but have not landed in
			// history yet, within a 60s grace
			for (const e of prev) {
				if (bazelHistIds.has(e.id) || activeIds.has(e.id)) continue;
				const dropped = e.droppedAt ?? now;
				if (now - dropped <= 60_000) out.set(e.id, { ...e, droppedAt: dropped });
			}
			// current in-flight invocations not yet in history
			for (const inv of activeBazel) {
				if (!bazelHistIds.has(inv.id)) out.set(inv.id, inv);
			}
			const next = [...out.values()];
			// Keep the previous reference when nothing changed so this never drives
			// a re-render loop.
			const same =
				next.length === prev.length &&
				next.every((e, i) => prev[i]?.id === e.id && prev[i]?.droppedAt === e.droppedAt);
			return same ? prev : next;
		});
	}, [activeBazel, bazelHistIds]);
	// Combine xcode builds and Bazel invocations into one chronological history
	// by start time, including cache operations whose IDs have different prefixes.
	const combinedHistorical = useMemo(() => {
		const items: HistEntry[] = [
			...historicalList.map((log): HistEntry => ({ kind: "historical", log })),
			...bazelList.map((log): HistEntry => ({ kind: "bazel", log })),
		];
		items.sort((a, b) => histStartMs(a) - histStartMs(b));
		return items;
	}, [historicalList, bazelList]);

	// Merge live + combined history into a single list. The live exec (if any)
	// sits at the top; history is newest-first below it. Dedupe by id so a build
	// that just finished (briefly present in both streams) isn't rendered twice.
	const merged = useMemo(() => {
		const ids = new Set<string>();
		const result: Array<
			| { kind: "live"; exec: LiveExec }
			| { kind: "bazel-live"; summary: ActiveBazelInvocation }
			| HistEntry
		> = [];
		if (liveExec) {
			result.push({ kind: "live", exec: liveExec });
			ids.add(liveExec.id);
		}
		for (const inv of bridgedBazel) {
			if (ids.has(inv.id)) continue;
			result.push({ kind: "bazel-live", summary: inv });
			ids.add(inv.id);
		}
		for (const entry of [...combinedHistorical].reverse()) {
			if (ids.has(entry.log.id)) continue;
			result.push(entry);
			ids.add(entry.log.id);
		}
		return result;
	}, [liveExec, bridgedBazel, combinedHistorical]);
	const [buildEntries, cacheEntries] = useMemo(() => {
		const builds: typeof merged = [];
		const caches: typeof merged = [];
		for (const entry of merged) {
			if (entry.kind === "historical" && cacheOperationLabel(entry.log.id)) {
				caches.push(entry);
			} else {
				builds.push(entry);
			}
		}
		return [builds, caches];
	}, [merged]);

	// The id that identifies a build entry across all kinds (an xcode exec or a Bazel
	// invocation). Selection, the deep-link, auto-select, and the row keys all resolve
	// a build through this single mapping.
	const entryId = useCallback(
		(m: (typeof merged)[number]): string =>
			m.kind === "live" ? m.exec.id : m.kind === "bazel-live" ? m.summary.id : m.log.id,
		[],
	);

	const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
	const selected = compact ? buildEntries[0] : merged.find((m) => entryId(m) === selectedId);
	const selectedHistoricalLog =
		selected && (selected.kind === "historical" || selected.kind === "bazel")
			? selected.log
			: undefined;
	const {
		lines: historicalLines,
		meta: historicalMeta,
		error: historicalError,
	} = useHistoricalLogContent(selectedHistoricalLog);

	// Stream the selected running invocation's log live (empty otherwise). The
	// end frame's terminal status settles the bridged entry immediately, so the
	// badge and log do not linger on RUNNING until the historical record lands.
	const settleBazel = useCallback((id: string, status: string) => {
		setBridgedBazel((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
	}, []);
	const selectedBazelLiveId =
		selected?.kind === "bazel-live" ? selected.summary.id : undefined;
	const bazelLiveLines = useLiveBazelExec(
		instanceReady ? apiUrl : undefined,
		instanceReady ? instanceToken : undefined,
		selectedBazelLiveId,
		settleBazel,
	);

	const getSelectedText = useCallback((): string => {
		const lines =
			selected?.kind === "live"
				? selected.exec.lines
				: selected?.kind === "bazel-live"
					? bazelLiveLines
					: historicalLines !== "loading"
						? historicalLines
						: [];
		// Only live xcode emits entries without their own line delimiters.
		const perLine = selected?.kind === "live";
		return joinChunks(lines, perLine, true);
	}, [selected, historicalLines, bazelLiveLines]);

	const [copied, setCopied] = useState(false);
	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(getSelectedText()).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}).catch(() => {
			// Clipboard API can fail in non-secure contexts
		});
	}, [getSelectedText]);

	const handleDownload = useCallback(() => {
		if (!selected) return;
		const text = getSelectedText();
		const blob = new Blob([text], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${entryId(selected)}.txt`;
		a.click();
		URL.revokeObjectURL(url);
	}, [selected, getSelectedText, entryId]);

	// Open the latest build by default, or a cache operation when no builds exist.
	useEffect(() => {
		const firstEntry = buildEntries[0] ?? cacheEntries[0];
		if (!firstEntry) {
			if (selectedId) setSelectedId(undefined);
			return;
		}
		if (selected) return;
		setSelectedId(entryId(firstEntry));
	}, [buildEntries, cacheEntries, selected, selectedId, entryId]);

	const logOutput = selected ? (
		selected.kind === "live" ? (
			<XtermLogPanel
				key={`live-${selected.exec.id}`}
				lines={selected.exec.lines}
				perLine
				finished={isTerminalStatus(selected.exec.status)}
			/>
		) : selected.kind === "bazel-live" ? (
			<XtermLogPanel
				key={`live-${selected.summary.id}`}
				lines={bazelLiveLines}
				finished={isTerminalStatus(selected.summary.status)}
			/>
		) : historicalError ? (
			<p className="p-4 text-sm text-red-600">Failed to load log: {historicalError.message}</p>
		) : historicalLines === "loading" ? (
			<p className="p-4 text-sm text-muted-foreground">Loading...</p>
		) : (
			<XtermLogPanel key={`hist-${selected.log.id}`} lines={historicalLines} finished />
		)
	) : null;
	if (compact) {
		const status =
			selected?.kind === "live"
				? selected.exec.status
				: selected?.kind === "bazel-live"
					? selected.summary.status
					: selected?.log.status;
		const title = !status
			? "Xcode builder connected"
			: status.toUpperCase() === "SUCCEEDED"
				? "Build succeeded"
				: status.toUpperCase() === "FAILED"
					? "Build failed"
					: isTerminalStatus(status)
						? "Build stopped"
						: "Building on Limrun";
		return (
			<section aria-label="Cloud build activity" className="flex min-w-0 flex-col gap-4">
				<div>
					<p className="mb-3 text-xs text-muted-foreground">Your cloud build</p>
					<h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
					<p className="mt-2 text-sm leading-6 text-muted-foreground">
						Build output from your cloud Xcode instance.
					</p>
				</div>
				<div className="flex h-[45svh] min-h-64 flex-col overflow-hidden rounded-lg border bg-background lg:h-[50svh]">
					<div className="flex items-center justify-between gap-2 border-b px-3 py-2">
						<span className="text-xs text-muted-foreground" role="status">
							{reconnecting && status && !isTerminalStatus(status)
								? "Reconnecting to build output..."
								: "Build output"}
						</span>
						{status && <ExecStatusBadge status={status} />}
					</div>
					{logOutput ?? (
						<p role="status" className="p-4 text-sm text-muted-foreground">
							{historyError || instanceError
								? "Could not load build activity. Retrying..."
								: instanceTerminated
									? "The builder stopped before any build logs were saved."
									: "Waiting for the first build. Output will appear here automatically."}
						</p>
					)}
				</div>
				<Link
					to={`/builds/${instanceId}`}
					className="self-start text-xs text-muted-foreground hover:text-foreground"
				>
					View all build logs <span aria-hidden>↗</span>
				</Link>
			</section>
		);
	}

	return (
		<AppShell title="Build logs" viewport>
			<PageHeader title="Build logs" description={<span className="font-mono text-xs">{instanceId}</span>} actions={canInstallToRealDevice && !isGradle && (
				<div className="ml-auto">
					<DeviceInstallRelay
						registryApiUrl={registryApiUrl}
						registryToken={auth.token ?? undefined}
						organizationId={organizationTid}
					/>
				</div>
			)} />
			{!isGradle && instanceId && organizationTid && (
				<BuildCacheStatus instanceId={instanceId} organizationTid={organizationTid} />
			)}
			{instance === "loading" && instanceNotFound && merged.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					Instance not found in any of your organizations. It may have
					been created moments ago or already purged.
				</p>
			) : (instance === "loading" && merged.length === 0) || historicalLoading ? (
				<p className="text-sm text-muted-foreground">Loading...</p>
			) : instanceStarting ? (
				<p className="text-sm text-muted-foreground">
					Instance is starting up, check back soon.
				</p>
			) : instanceTerminated && merged.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					Instance has been terminated and no build logs are
					available.
				</p>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden lg:flex-row">
						<div className="flex h-40 min-h-0 w-full shrink-0 flex-col gap-4 lg:h-auto lg:w-64">
							{[
								{ title: "Build activity", entries: buildEntries, isCache: false },
								...(cacheEntries.length ? [{ title: "Cache activity", entries: cacheEntries, isCache: true }] : []),
							].map(({ title, entries, isCache }) => (
								<section
									key={title}
									aria-label={title}
									className={cn(
										"flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background",
										isCache ? "max-h-[45%] shrink-0" : "flex-1",
									)}
								>
									<div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
										<h2 className="flex items-center gap-2 text-sm font-semibold">
											{isCache && <Database className="h-3.5 w-3.5 text-brand" aria-hidden="true" />}
											{title}
										</h2>
										{entries.length > 0 && <span className="text-xs text-muted-foreground">{entries.length}</span>}
									</div>
									<ScrollArea className="min-h-0 flex-1">
										{entries.length === 0 ? (
											<p className="p-3 text-sm text-muted-foreground">No builds yet.</p>
										) : (
											entries.map((entry, i) => {
												const id = entryId(entry);
												const status =
													entry.kind === "live"
														? entry.exec.status
														: entry.kind === "bazel-live"
															? entry.summary.status
															: entry.log.status;
												const isLive =
													entry.kind === "live" || entry.kind === "bazel-live";
												const liveStartedAt =
													entry.kind === "live"
														? entry.exec.startedAt
														: entry.kind === "bazel-live"
															? entry.summary.startedAt
															: undefined;
												const startedAt =
													entry.kind === "historical" || entry.kind === "bazel"
														? entry.log.startedAt
														: undefined;
												const finishedAt =
													entry.kind === "historical"
														? entry.log.finishedAt
														: undefined;
												const durationLabel =
													entry.kind === "historical"
														? startedAt
															? formatDuration(startedAt, finishedAt)
															: undefined
														: entry.kind === "bazel"
															? formatDurationMs(entry.log.durationMs)
															: undefined;
												return (
													<div key={id}>
														{i > 0 && (
															<div className="border-t" />
														)}
														<button
															type="button"
															onClick={() => setSelectedId(id)}
															className={cn(
																"relative flex w-full flex-col gap-1.5 px-3 py-3 text-left transition-colors hover:bg-muted/50",
																id === selectedId && "bg-muted",
																isCache && id === selectedId && "before:absolute before:inset-y-3 before:left-0 before:w-0.5 before:rounded-full before:bg-brand",
																isLive && status.toUpperCase() === "RUNNING" && "border-l-2 border-l-blue-400",
															)}
														>
															<div className="flex items-center justify-between gap-2">
																<ExecStatusBadge status={status} />
																{startedAt && (
																	<span className="shrink-0 text-xs text-muted-foreground">
																		{formatRelativeTime(startedAt)}
																	</span>
																)}
															</div>
															{isLive ? (
																<div className="flex items-center justify-between text-xs text-muted-foreground">
																	<span className="font-mono font-medium">
																		{formatBuildLabel(i, entries.length)}
																	</span>
																	{liveStartedAt && status.toUpperCase() === "RUNNING" && (
																		<LiveTimer startedAt={liveStartedAt} />
																	)}
																</div>
															) : (
																<div className="flex items-center justify-between text-xs text-muted-foreground">
																	<TooltipProvider>
																		<Tooltip delayDuration={0}>
																			<TooltipTrigger asChild>
																				<button
																					type="button"
																					className={cn(
																						"rounded px-1 py-0.5 -mx-1 -my-0.5 font-medium hover:bg-muted hover:text-foreground",
																						isCache ? "text-foreground" : "font-mono",
																					)}
																					onClick={(e) => {
																						e.stopPropagation();
																						navigator.clipboard.writeText(id).catch(() => { });
																					}}
																				>
																					{cacheOperationLabel(id) ?? formatBuildLabel(i, entries.length)}
																				</button>
																			</TooltipTrigger>
																			<TooltipContent side="right">{id}</TooltipContent>
																		</Tooltip>
																	</TooltipProvider>
																	{durationLabel && (
																		<span className="shrink-0">
																			{durationLabel}
																		</span>
																	)}
																</div>
															)}
														</button>
													</div>
												);
											})
										)}
									</ScrollArea>
								</section>
							))}
						</div>

						{/* Log viewer */}
						<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-background">
							<div className="flex flex-col gap-1 border-b px-3 py-2">
								<div className="flex min-w-0 items-center gap-2">
									<h2 className="shrink-0 text-sm font-semibold">
										{selected?.kind === "bazel" || selected?.kind === "bazel-live"
											? "RBE invocation"
											: selected?.kind === "historical"
												? cacheOperationLabel(selected.log.id) ?? "Output"
												: "Output"}
									</h2>
									{selected && (
										<ExecStatusBadge
											status={
												selected.kind === "live"
													? selected.exec.status
													: selected.kind === "bazel-live"
														? selected.summary.status
														: selected.log.status
											}
										/>
									)}
									{selected && (selected.kind === "live" || selected.kind === "bazel-live" || historicalLines !== "loading") && (
										<div className="ml-auto flex shrink-0 items-center gap-1">
											<TooltipProvider>
												<Tooltip delayDuration={0}>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-7 w-7"
															onClick={handleCopy}
															aria-label="Copy to clipboard"
														>
															{copied ? (
																<Check className="h-3.5 w-3.5 text-green-600" />
															) : (
																<Copy className="h-3.5 w-3.5" />
															)}
														</Button>
													</TooltipTrigger>
													<TooltipContent>Copy to clipboard</TooltipContent>
												</Tooltip>
											</TooltipProvider>
											<TooltipProvider>
												<Tooltip delayDuration={0}>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-7 w-7"
															onClick={handleDownload}
															aria-label="Download log"
														>
															<Download className="h-3.5 w-3.5" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>Download log</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										</div>
									)}
								</div>
								{selected?.kind === "historical" && (
									<div className="flex min-w-0 items-center gap-3 text-xs text-muted-foreground">
										{selected.log.startedAt && (
											<span className="shrink-0">{new Date(selected.log.startedAt).toLocaleString()}</span>
										)}
										{selected.log.buildDurationMs != null && (
											<span className="shrink-0">{formatDurationMs(selected.log.buildDurationMs)}</span>
										)}
										{selected.log.error && !/^exit status \d+$/.test(selected.log.error) && (
											<span className="truncate text-red-600" title={selected.log.error}>
												{selected.log.error}
											</span>
										)}
										{selected.log.exitCode != null && selected.log.exitCode !== 0 &&
											(!selected.log.error || /^exit status \d+$/.test(selected.log.error)) && (
												<span className="shrink-0 text-red-600">exit code {selected.log.exitCode}</span>
											)}
									</div>
								)}
								{selected?.kind === "bazel" && (
									<div className="flex min-w-0 items-center gap-3 text-xs text-muted-foreground">
										{selected.log.startedAt && (
											<span className="shrink-0">{new Date(selected.log.startedAt).toLocaleString()}</span>
										)}
										{selected.log.durationMs != null && (
											<span className="shrink-0">{formatDurationMs(selected.log.durationMs)}</span>
										)}
										{selected.log.pattern && selected.log.pattern.length > 0 && (
											<span className="truncate font-mono" title={selected.log.pattern.join(" ")}>
												{selected.log.pattern.join(" ")}
											</span>
										)}
										{historicalMeta?.actions &&
											(() => {
												const a = historicalMeta.actions;
												const c = cacheStats(a, selected.log.status);
												return (
													<span className="shrink-0">
														{a.total} action{a.total === 1 ? "" : "s"}
														{` · ${cacheLabel(c)}`}
													</span>
												);
											})()}
										{selected.log.error && (
											<span className="truncate text-red-600" title={selected.log.error}>
												{selected.log.error}
											</span>
										)}
									</div>
								)}
							</div>
							{!selected ? (
								<ScrollArea className="flex-1">
									<div className="rounded-b-lg bg-muted/50 p-4">
										<p className="text-sm text-muted-foreground">
											Select a build to view output.
										</p>
									</div>
								</ScrollArea>
							) : (
								<div className="flex min-h-0 flex-1 flex-col">
									{/* The log is the page. The header above already carries the
										    lifecycle details (status, timestamps, duration, errors);
										    Bazel's rich metrics sit in a collapsible strip on top of
										    the log, capped in height so the log stays visible. */}
									{selected.kind === "bazel" && historicalMeta && (
										<Collapsible
											defaultOpen
											className="flex max-h-[45%] shrink-0 flex-col border-b"
										>
											<CollapsibleTrigger className="group flex shrink-0 items-center gap-1 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
												<ChevronRight className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90" />
												Overview
											</CollapsibleTrigger>
											<CollapsibleContent className="min-h-0 overflow-y-auto">
												<BazelOverview meta={historicalMeta} />
											</CollapsibleContent>
										</Collapsible>
									)}
									{logOutput}
								</div>
							)}
						</div>
					</div>
				</>
			)}
		</AppShell>
	);
}

function OverviewSection({ title, children }: { title: string; children: ReactNode }) {
	return (
		<section className="flex flex-col gap-2">
			<h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
				{title}
			</h3>
			{children}
		</section>
	);
}

// MetricBars renders labeled counts as proportional bars; the largest count fills
// the track and the rest scale to it. Used for the runner and mnemonic breakdowns,
// colored by the tones in lib/bazel.
function MetricBars({
	rows,
}: {
	rows: { name: string; count: number; tone?: BarTone }[];
}) {
	const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
	return (
		<div className="flex flex-col gap-1.5">
			{rows.map((r) => (
				<div key={r.name} className="flex items-center gap-2 text-xs">
					<span
						className="w-44 shrink-0 truncate font-mono text-muted-foreground sm:w-64"
						title={r.name}
					>
						{r.name}
					</span>
					<div className="h-3 flex-1 overflow-hidden rounded bg-muted">
						<div
							className={cn("h-full rounded", barToneClass[r.tone ?? "accent"])}
							style={{ width: `${(r.count / max) * 100}%` }}
						/>
					</div>
					<span className="w-10 shrink-0 text-right tabular-nums">{r.count}</span>
				</div>
			))}
		</div>
	);
}

// BazelOverview is the rich RBE invocation summary shown above the log: the
// remote-execution payoff (runner breakdown), action types, timing, and artifact
// metadata. Each section renders only when its data is present, so an INCOMPLETE
// build degrades cleanly.
export function BazelOverview({ meta }: { meta: BazelInvocationMeta }) {
	const actions = meta.actions;
	const runners = actions?.runners ?? [];
	const mnemonics = actions?.mnemonics ?? [];
	const cache = actions ? cacheStats(actions, meta.status) : undefined;
	return (
		<div className="flex flex-col gap-5 p-4">
			{actions && runners.length > 0 && cache && (
				<OverviewSection title="Remote execution">
					{cache.kind !== "rate" ? (
						<p className="text-sm text-muted-foreground">
							{cache.kind === "upToDate"
								? "Up to date. No actions needed the remote cache."
								: "No actions reached remote execution."}
						</p>
					) : (
						<p className="text-sm">
							<span className="font-semibold text-emerald-600">{cache.hits}</span> of{" "}
							<span className="font-semibold">{cache.cacheable}</span> cacheable actions served from
							remote cache <span className="text-muted-foreground">({cache.ratePct}%)</span>
						</p>
					)}
					<MetricBars
						rows={runners.map((r) => ({ name: r.name, count: r.count, tone: runnerTone(r.name) }))}
					/>
				</OverviewSection>
			)}
			{mnemonics.length > 0 && (
				<OverviewSection title="Action types">
					{/* Recorder emits mnemonics ranked by count, so render as-is. */}
					<MetricBars
						rows={mnemonics.map((m) => ({ name: m.name, count: m.count, tone: "accent" as const }))}
					/>
				</OverviewSection>
			)}
			<OverviewSection title="Timing">
				<dl className="flex gap-8 text-sm">
					<div>
						<dt className="text-xs text-muted-foreground">Wall</dt>
						<dd className="tabular-nums">{formatDurationMs(meta.durationMs) ?? "-"}</dd>
					</div>
					<div>
						<dt className="text-xs text-muted-foreground">CPU</dt>
						<dd className="tabular-nums">{formatDurationMs(meta.cpuTimeMs) ?? "-"}</dd>
					</div>
				</dl>
			</OverviewSection>
			{meta.artifacts && meta.artifacts.length > 0 && (
				<OverviewSection title="Artifacts">
					<div className="flex flex-col gap-1">
						{meta.artifacts.map((a, i) => (
							<div key={i} className="flex items-center gap-3 text-xs">
								<span className="font-mono">{a.name}</span>
								<span className="text-muted-foreground">{formatSize(a.sizeBytes)}</span>
								<span
									className="truncate font-mono text-muted-foreground"
									title={a.digest}
								>
									{a.digest.slice(0, 12)}
								</span>
							</div>
						))}
					</div>
				</OverviewSection>
			)}
		</div>
	);
}
